import { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline, Search } from '@mui/icons-material'
import {
  Filter,
  TableHeadWithoutOrdenation,
  CustomTableCell,
  FilterDrawer,
  Ordinator,
  OrdinatorDrawer,
  GroupedFilterTags,
} from '../../components'
import { useVisualManagementDrawer } from '../../contexts/VisualManagementDrawerContext'
import useVisualManagementGraph from '../../hooks/useVisualManagementGraph'
import { ColumnSeletor } from './ColumnSeletor/ColumnSeletor'
import { ColumnDrawer } from './ColumnDrawer/ColumnDrawer'
import { useEmployeeContext } from '../../contexts/EmployeeContext'
import { PLANS } from '../../utils/planNames'
import { getDDMMByExtraDate } from '../../utils/dateConversion'
import filterDrawerHelper from '../../utils/filterDrawer'
import useViewport from '../../hooks/useViewport'
import { useTranslation } from "react-i18next"

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px 0;
  gap: 8px;
`

const SearchContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid #d6d7db;
  border-radius: 4px;
  padding: 4px 8px;
  height: 40px;
  gap: 8px;
`

const StyledInput = styled.input`
  border: 0;
  width: 100%;
  outline: none;
`

const StickyTableCell = styled(TableCell)`
  min-width: 50px;
  left: 0;
  position: sticky;
  z-index: 2;
  background-color: #fff;
`

const now = ({ extraHour }) => {
  let dateNow = Date.now()

  if (extraHour) {
    dateNow = dateNow + 1000 * 60 * 60 * extraHour
  }

  let date = new Date(dateNow).getHours()

  if (date < 10) {
    return `0${date}`
  }

  return date
}

const rowsPerPage = 10

const VisualManagement = ({ planName }) => {
  const [page, setPage] = useState(0)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const [openOrderDrawer, setOpenOrderDrawer] = useState(false)
  const {
    COLUMNS,
    columnsSelected,
    setColumnsSelected,
    loading: loadingColumns,
  } = useVisualManagementDrawer()
  const {
    loading: loadingData,
    rows,
    filterOptions,
    setSelectedFilterOptions,
    inputValue,
    handleType,
    selectedFilterOptions,
    onSelectOrderSave,
  } = useVisualManagementGraph({ planName })
  const { setEmployee } = useEmployeeContext()
  const { width } = useViewport()
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper
  const { t } = useTranslation('common')

  const highFatigueTooltip = t('supervisor_dashboard.tooltip.high_fatigue')
  const notificationTooltip = t('supervisor_dashboard.tooltip.notification')
  const counterMeasureTooltip = t('supervisor_dashboard.tooltip.fatigue_level')

  const baseHeadState = [
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('supervisor_dashboard.header.colaborator'),
      align: 'left',
      sticky: true,
    },
    {
      id: 'intakePending',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.intake_pending'),
      align: 'left',
    },
    {
      id: 'highFatigue',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.high_fatigue'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={highFatigueTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      ),
    },
    {
      id: 'notification',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.notification'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={notificationTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      )
    },
    {
      id: 'counterMeasure',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.counter_measure'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={counterMeasureTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      )
    },
    {
      id: 'fatigueLevel8h0',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: null })}h`,
      align: 'center',
    },
  ]

  const baseHeadPredState = [
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('supervisor_dashboard.header.colaborator'),
      align: 'left',
      sticky: true,
    },
    {
      id: 'intakePending',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.intake_pending'),
      align: 'left',
    },
    {
      id: 'highFatigue',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.high_fatigue'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={highFatigueTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      ),
    },
    {
      id: 'notification',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.notification'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={notificationTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      )
    },
    {
      id: 'fatigueLevel8h0',
      numeric: false,
      disablePadding: false,
      label: `${now({ extraHour: 0 })}h`,
      align: 'center',
    },
  ]

  const generateInitialHeadState = () => {
    let elements = []
    let extraDays = 0

    for (let index = 2; index < 98; index++) {
      if (now({ extraHour: index - 1 }) === `00`) {
        extraDays = extraDays + 1
      }

      elements.push({
        id: `fatigueLevel8h${index}`,
        numeric: false,
        disablePadding: false,
        label:
          `${now({ extraHour: index - 1 })}` !== '00'
            ? `${now({ extraHour: index - 1 })}h`
            : `${getDDMMByExtraDate({ extraDate: extraDays })} 00h`,
        align: 'center',
      })
    }

    return elements
  }

  const generatedInitialHeadState = generateInitialHeadState()
  const initialHeadState = [...baseHeadState, ...generatedInitialHeadState]

  const initialHeadPredState = [
    ...baseHeadPredState,
    ...generatedInitialHeadState,
  ]

  const initialHeadStateNow = [
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('supervisor_dashboard.header.colaborator'),
      align: 'left',
    },
    {
      id: 'intakePending',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.intake_pending'),
      align: 'left',
    },
    {
      id: 'highFatigue',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.high_fatigue'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={highFatigueTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      ),
    },
    {
      id: 'notification',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.notification'),
      align: 'left',
      tooltip: (
        <Tooltip
          title={notificationTooltip}
          placement='right'
          arrow
        >
          <HelpOutline fontSize='18' />
        </Tooltip>
      )
    },
    {
      id: 'fatigueLevel8h',
      numeric: false,
      disablePadding: false,
      label: t('supervisor_dashboard.header.fatigue_level'),
      align: 'center',
    },
  ]

  const getHeadState = ({ planName }) => {
    if (planName === PLANS.OPTIMIZED) return initialHeadState
    if (planName === PLANS.PREDICTION) return initialHeadPredState
    if (planName === PLANS.NOW) return initialHeadStateNow
  }

  const [headCells, setHeadCells] = useState(getHeadState({ planName }))

  useEffect(() => {
    if (columnsSelected) {
      let fatigueLevelHours = columnsSelected['fatigueLevel8h']

      const headColumns = getHeadState({ planName })

      const newColunms = headColumns.filter(item => {
        if (columnsSelected[item.id]) return item

        if (
          fatigueLevelHours &&
          planName !== PLANS.NOW &&
          item.id?.includes('fatigueLevel8h')
        )
          return item
      })

      setHeadCells(newColunms)
    }
  }, [columnsSelected])

  useEffect(() => {
    setPage(0)
  }, [selectedFilterOptions])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () => [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows],
  )

  const renderFatigueColumns = ({ row }) => {
    let elements = []
    let rangeStart = null // Track the start of the current `isWorking` range

    for (let index = 0; index < 97; index++) {
      const fatigueString = `fatigueLevel8h${index}`
      const isWorking = row[fatigueString]?.isWorking

      // Track the start of a new `isWorking` range
      if (isWorking && rangeStart === null) {
        rangeStart = index
      }

      // End of a range
      if ((!isWorking || index === 96) && rangeStart !== null) {
        const rangeEnd = index - (isWorking ? 0 : 1)

        // Apply left border at rangeStart and right border at rangeEnd
        for (let i = rangeStart; i <= rangeEnd; i++) {
          const fatigueStr = `fatigueLevel8h${i}`
          const working = row[fatigueStr]?.isWorking

          elements.push({
            column: (
              <CustomTableCell
                type='value'
                data={row[fatigueStr]?.fatigueLevel}
                width={90}
                align='center'
                isSleeping={row[fatigueStr]?.isSleeping}
                isWorking={working}
                style={{
                  borderTop: working ? '2px solid black' : undefined,
                  borderBottom: working ? '2px solid black' : undefined,
                  borderLeft: i === rangeStart ? '2px solid black' : undefined,
                  borderRight: i === rangeEnd ? '2px solid black' : undefined,
                }}
              />
            ),
          })
        }

        // Reset rangeStart after processing the range
        rangeStart = null
      }

      // Handle non-working cells outside ranges
      if (!isWorking && rangeStart === null) {
        elements.push({
          column: (
            <CustomTableCell
              type='value'
              data={row[fatigueString]?.fatigueLevel}
              width={90}
              align='center'
              isSleeping={row[fatigueString]?.isSleeping}
              isWorking={isWorking}
            />
          ),
        })
      }
    }

    return elements
  }

  return (
    <>
      <Container>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: 20, fontWeight: 600 }}>{t('supervisor_dashboard.title')}</span>
          <Tooltip
            title={t('supervisor_dashboard.tooltip.main')}
            placement='right'
            arrow
          >
            <HelpOutline fontSize='18' />
          </Tooltip>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <FiltersContainer>
            <SearchContainer>
              <Search sx={{ color: '#858B92' }} />
              <StyledInput
                style={{ border: 0 }}
                placeholder='Buscar'
                value={inputValue}
                onChange={handleType}
              />
            </SearchContainer>
            <div style={{ display: 'flex', gap: 8 }}>
              <Filter onClick={() => setOpenFilterDrawer(prev => !prev)} />
              <Ordinator onClick={() => setOpenOrderDrawer(prev => !prev)} />
              <ColumnSeletor onClick={() => setOpenDrawer(prev => !prev)} />
            </div>
          </FiltersContainer>
          {selectedFilterOptionsList({
            selectedFilterOptions,
          }).length > 0 && (
              <GroupedFilterTags
                tags={selectedFilterOptionsList({
                  selectedFilterOptions,
                })}
                onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                maxTagsToShow={getMaxTagsToShow({ width })}
              />
            )}
        </div>
        <TableContainer>
          {!loadingColumns && (
            <Table
              sx={{
                minWidth: 750,
                '& th, & td': {
                  textAlign: 'left',
                }

              }}
              aria-labelledby='tableTitle'
            >
              <TableHeadWithoutOrdenation
                headCells={headCells}
              />
              <TableBody>
                {!loadingData ? (
                  <>
                    {rows.length > 0 ? (
                      <>
                        {visibleRows.map((row, index) => {
                          const labelId = `enhanced-table-checkbox-${index}`

                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={row.id}
                            >
                              <StickyTableCell
                                component='td'
                                id={labelId}
                                scope='row'
                                padding='none'
                                sx={{ cursor: 'pointer' }}
                                width={120}
                                onClick={() => {
                                  setEmployee({
                                    id: row.id,
                                    name: row.employee,
                                  })
                                }}
                              >
                                {row.employee}
                              </StickyTableCell>
                              {columnsSelected[COLUMNS.INTAKE] && (
                                <CustomTableCell
                                  data={row.intakePending}
                                  type='action'
                                />
                              )}
                              {columnsSelected[COLUMNS.HIGH_FATIGUE] && (
                                <CustomTableCell
                                  data={row.highFatigue}
                                  type='hour'
                                  sxx={{ minWidth: 105, width: 105 }}
                                />
                              )}
                              {columnsSelected[COLUMNS.NOTIFICATION] && (
                                <CustomTableCell
                                  data={row.notification}
                                  type='action'
                                />
                              )}
                              {planName === PLANS.OPTIMIZED &&
                                columnsSelected[COLUMNS.COUNTER_MEASURE] && (
                                  <CustomTableCell
                                    data={row.counterMeasure}
                                    type='action'
                                  />
                                )}
                              {columnsSelected[COLUMNS.FATIGUE_LEVEL_8H] &&
                                planName === PLANS.NOW && (
                                  <CustomTableCell
                                    type='value'
                                    data={row.fatigueLevel8h?.fatigueLevel}
                                    align='left'
                                    planName={planName}
                                    isSleeping={row.fatigueLevel8h?.isSleeping}
                                    isWorking={row.fatigueLevel8h?.isWorking}
                                  />
                                )}
                              {columnsSelected[COLUMNS.FATIGUE_LEVEL_8H] &&
                                planName !== PLANS.NOW && (
                                  <>
                                    {renderFatigueColumns({ row }).map(
                                      ({ column }) => column,
                                    )}
                                  </>
                                )}
                            </TableRow>
                          )
                        })}
                        {emptyRows() > 0 && (
                          <TableRow
                            style={{
                              height: 53 * emptyRows(),
                            }}
                          >
                            <TableCell colSpan={loadingData ? 16 : 100} />
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <TableRow style={{ height: 53 * 10 }}>
                        <TableCell
                          colSpan={loadingData ? 16 : 100}
                          align='center'
                        >
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={loadingData ? 16 : 100}
                      align='center'
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component='div'
          count={rows.length}
          showFirstButton
          showLastButton
          labelRowsPerPage={''}
          rowsPerPageOptions={[]}
          rowsPerPage={10}
          page={page}
          onPageChange={handleChangePage}
          labelDisplayedRows={({ to, count }) =>
            `${to} ${t('of')} ${count} ${t('colaborators')}`
          }
        />
      </Container>

      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={setSelectedFilterOptions}
        loading={loadingData}
        options={filterOptions}
        withFatigueLevels
        withCounterMeasure
        withSeverity
        withShift
        withPosition
        withTeam
        withNotificationStatus
      />

      <ColumnDrawer
        open={openDrawer}
        onClose={() => {
          setOpenDrawer(false)
        }}
        onColumnSave={setColumnsSelected}
        COLUMNS={COLUMNS}
        planName={planName}
        withPendingIntake
      />

      <OrdinatorDrawer
        open={openOrderDrawer}
        onClose={() => {
          setOpenOrderDrawer(false)
        }}
        onOrderSave={onSelectOrderSave}
      />
    </>
  )
}

export default VisualManagement
